import React, { useState, useEffect } from 'react';
import './App.css';
import { supabase } from './supabase';

const API = 'https://church-app-tp0x.onrender.com';

function App() {
  const [session, setSession] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [members, setMembers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [emailTo, setEmailTo] = useState('');
  const [emailToName, setEmailToName] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [audienceType, setAudienceType] = useState('individual');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({
    first_name: '', last_name: '', email: '', phone: '', campus_id: '', status: 'visitor'
  });
  const [memberStatus, setMemberStatus] = useState('');
  const [newTag, setNewTag] = useState({ name: '', color: '#1F3D34' });
  const [tagStatus, setTagStatus] = useState('');
  const [memberTags, setMemberTags] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
    })
    supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })
  }, [])

  useEffect(() => {
    if (!session) return
    fetch(`${API}/members`)
      .then(res => res.json())
      .then(data => {
        setMembers(data)
        data.forEach(m => loadMemberTags(m.id))
      });
    fetch(`${API}/campuses`)
      .then(res => res.json())
      .then(data => setCampuses(data));
    fetch(`${API}/tags`)
      .then(res => res.json())
      .then(data => setTags(data));
    fetch(`${API}/stats`)
      .then(res => res.json())
      .then(data => setStats(data));
    fetch(`${API}/email-history`)
      .then(res => res.json())
      .then(data => setEmailHistory(data));
  }, [session]);

  const login = async () => {
    setLoginError('')
    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword
    })
    if (error) setLoginError(error.message)
  }

  const logout = async () => {
    await supabase.auth.signOut()
    setSession(null)
  }

  const filteredMembers = members.filter(m =>
    `${m.first_name} ${m.last_name}`.toLowerCase().includes(memberSearch.toLowerCase())
  )

  const getAudience = () => {
    if (audienceType === 'individual') return [emailTo]
    if (audienceType === 'all') return members.map(m => m.email).filter(Boolean)
    if (audienceType === 'campus') return members
      .filter(m => m.campus_id === parseInt(selectedCampus))
      .map(m => m.email)
      .filter(Boolean)
    if (audienceType === 'tag') {
      const taggedMemberIds = Object.entries(memberTags)
        .filter(([, mtags]) => mtags.some(t => t && t.id === parseInt(selectedTag)))
        .map(([id]) => parseInt(id))
      return members
        .filter(m => taggedMemberIds.includes(m.id))
        .map(m => m.email)
        .filter(Boolean)
    }
    return []
  }

  const sendEmail = async () => {
    const audience = getAudience()
    if (audience.length === 0) {
      setEmailStatus('No recipients found ❌')
      return
    }
    setEmailStatus(`Sending to ${audience.length} recipient(s)...`)
    let successCount = 0
    for (const email of audience) {
      const res = await fetch(`${API}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: email, subject: emailSubject, message: emailMessage })
      })
      const data = await res.json()
      if (data.success) successCount++
    }
    await fetch(`${API}/email-history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject: emailSubject,
        message: emailMessage,
        audience_type: audienceType,
        recipient_count: successCount
      })
    })
    fetch(`${API}/email-history`)
      .then(res => res.json())
      .then(data => setEmailHistory(data))
    setEmailStatus(`Sent successfully to ${successCount} recipient(s)! ✅`)
    setEmailTo('')
    setEmailToName('')
    setMemberSearch('')
    setEmailSubject('')
    setEmailMessage('')
  }

  const addMember = async () => {
    const res = await fetch(`${API}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newMember)
    })
    const data = await res.json()
    if (data && data[0]) {
      setMembers([...members, data[0]])
      setMemberStatus('Member added successfully! ✅')
      setNewMember({ first_name: '', last_name: '', email: '', phone: '', campus_id: '', status: 'visitor' })
      setShowAddMember(false)
    } else {
      setMemberStatus('Something went wrong ❌')
    }
  }

  const addTag = async () => {
    const res = await fetch(`${API}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newTag)
    })
    const data = await res.json()
    if (data && data[0]) {
      setTags([...tags, data[0]])
      setTagStatus('Tag created! ✅')
      setNewTag({ name: '', color: '#1F3D34' })
    } else {
      setTagStatus('Something went wrong ❌')
    }
  }

  const loadMemberTags = async (memberId) => {
    const res = await fetch(`${API}/member-tags/${memberId}`)
    const data = await res.json()
    setMemberTags(prev => ({ ...prev, [memberId]: data.map(mt => mt.tags) }))
  }

  const assignTag = async () => {
    if (!selectedMember || !selectedTagToAdd) return
    const res = await fetch(`${API}/member-tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ member_id: selectedMember, tag_id: selectedTagToAdd })
    })
    const data = await res.json()
    if (data && data[0]) {
      loadMemberTags(selectedMember)
      setSelectedTagToAdd('')
    }
  }

  if (!session) {
    return (
      <div className="login-screen">
        <div className="login-box">
          <h1 className="logo" style={{color: '#1F3D34', marginBottom: '8px'}}>ChurchOS</h1>
          <p style={{color: '#8FAF9C', marginBottom: '32px', fontSize: '14px'}}>Sign in to your dashboard</p>
          <input
            type="email"
            placeholder="Email"
            value={loginEmail}
            onChange={e => setLoginEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            value={loginPassword}
            onChange={e => setLoginPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && login()}
          />
          {loginError && <p style={{color: 'red', fontSize: '14px'}}>{loginError}</p>}
          <button onClick={login} className="send-btn">Sign In</button>
        </div>
      </div>
    )
  }

  return (
    <div className="app">
      <div className="sidebar">
        <h1 className="logo">ChurchOS</h1>
        <nav>
          <button onClick={() => setActiveTab('dashboard')} className={activeTab === 'dashboard' ? 'active' : ''}>Dashboard</button>
          <button onClick={() => setActiveTab('members')} className={activeTab === 'members' ? 'active' : ''}>Members</button>
          <button onClick={() => setActiveTab('campuses')} className={activeTab === 'campuses' ? 'active' : ''}>Campuses</button>
          <button onClick={() => setActiveTab('tags')} className={activeTab === 'tags' ? 'active' : ''}>Tags</button>
          <button onClick={() => setActiveTab('communications')} className={activeTab === 'communications' ? 'active' : ''}>Communications</button>
        </nav>
        <button onClick={logout} className="logout-btn">Sign Out</button>
      </div>

      <div className="main">
        {activeTab === 'dashboard' && (
          <div>
            <h2>Welcome back, Autumn 👋</h2>
            <div className="stats">
              <div className="stat-card">
                <h3>{stats?.totalMembers || 0}</h3>
                <p>Total Members</p>
              </div>
              <div className="stat-card">
                <h3>{stats?.totalCampuses || 0}</h3>
                <p>Campuses</p>
              </div>
              <div className="stat-card">
                <h3>{stats?.totalTags || 0}</h3>
                <p>Tags</p>
              </div>
              {stats?.topCampus && (
                <div className="stat-card">
                  <h3>{stats.topCampus.name}</h3>
                  <p>Most Members ({stats.topCampus.count})</p>
                </div>
              )}
            </div>
            {stats?.statusCounts && (
              <div className="status-breakdown">
                <h3>Members by Status</h3>
                <div className="status-grid">
                  {Object.entries(stats.statusCounts).map(([status, count]) => (
                    <div key={status} className="status-card">
                      <h4>{count}</h4>
                      <p>{status.charAt(0).toUpperCase() + status.slice(1)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'members' && (
          <div>
            <div className="tab-header">
              <h2>Members</h2>
              <button className="send-btn" onClick={() => setShowAddMember(!showAddMember)}>
                {showAddMember ? 'Cancel' : '+ Add Member'}
              </button>
            </div>
            {memberStatus && <p className="status">{memberStatus}</p>}
            {showAddMember && (
              <div className="comm-form" style={{marginBottom: '24px'}}>
                <h3>New Member</h3>
                <div className="form-row">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={newMember.first_name}
                    onChange={e => setNewMember({...newMember, first_name: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={newMember.last_name}
                    onChange={e => setNewMember({...newMember, last_name: e.target.value})}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Email"
                  value={newMember.email}
                  onChange={e => setNewMember({...newMember, email: e.target.value})}
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={newMember.phone}
                  onChange={e => setNewMember({...newMember, phone: e.target.value})}
                />
                <select
                  value={newMember.campus_id}
                  onChange={e => setNewMember({...newMember, campus_id: e.target.value})}>
                  <option value="">Select Campus...</option>
                  {campuses.map(campus => (
                    <option key={campus.id} value={campus.id}>{campus.name}</option>
                  ))}
                </select>
                <select
                  value={newMember.status}
                  onChange={e => setNewMember({...newMember, status: e.target.value})}>
                  <option value="visitor">Visitor</option>
                  <option value="member">Member</option>
                  <option value="volunteer">Volunteer</option>
                  <option value="staff">Staff</option>
                </select>
                <button onClick={addMember} className="send-btn">Save Member</button>
              </div>
            )}
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Tags</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => (
                  <tr key={member.id}>
                    <td>{member.first_name} {member.last_name}</td>
                    <td>{member.email}</td>
                    <td>{member.phone}</td>
                    <td>{member.status}</td>
                    <td>
                      <div className="member-tags-cell">
                        {(memberTags[member.id] || []).map(tag => tag && (
                          <span key={tag.id} className="tag-pill" style={{background: tag.color}}>
                            {tag.name}
                          </span>
                        ))}
                        {selectedMember === member.id ? (
                          <div className="tag-assign">
                            <select value={selectedTagToAdd} onChange={e => setSelectedTagToAdd(e.target.value)}>
                              <option value="">Add tag...</option>
                              {tags.map(tag => (
                                <option key={tag.id} value={tag.id}>{tag.name}</option>
                              ))}
                            </select>
                            <button onClick={assignTag} className="tag-add-btn">✓</button>
                            <button onClick={() => setSelectedMember(null)} className="tag-add-btn">✕</button>
                          </div>
                        ) : (
                          <button onClick={() => setSelectedMember(member.id)} className="tag-add-btn">+</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'campuses' && (
          <div>
            <h2>Campuses</h2>
            <div className="campus-grid">
              {campuses.map(campus => (
                <div key={campus.id} className="campus-card">
                  <h3>{campus.name}</h3>
                  <p>{campus.city}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'tags' && (
          <div>
            <div className="tab-header">
              <h2>Tags</h2>
            </div>
            <div className="comm-form" style={{marginBottom: '24px'}}>
              <h3>Create New Tag</h3>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Tag name (e.g. Volunteer)"
                  value={newTag.name}
                  onChange={e => setNewTag({...newTag, name: e.target.value})}
                />
                <input
                  type="color"
                  value={newTag.color}
                  onChange={e => setNewTag({...newTag, color: e.target.value})}
                />
              </div>
              <button onClick={addTag} className="send-btn">Create Tag</button>
              {tagStatus && <p className="status">{tagStatus}</p>}
            </div>
            <div className="tag-grid">
              {tags.map(tag => (
                <div key={tag.id} className="tag-card" style={{borderLeft: `4px solid ${tag.color}`}}>
                  <span className="tag-dot" style={{background: tag.color}}></span>
                  <span className="tag-name">{tag.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'communications' && (
          <div>
            <h2>Communications</h2>
            <div className="comm-form">
              <h3>Send Email</h3>
              <div className="audience-selector">
                <label>Audience</label>
                <div className="audience-options">
                  <button
                    className={audienceType === 'individual' ? 'active' : ''}
                    onClick={() => setAudienceType('individual')}>
                    Individual
                  </button>
                  <button
                    className={audienceType === 'all' ? 'active' : ''}
                    onClick={() => setAudienceType('all')}>
                    All Members ({members.length})
                  </button>
                  <button
                    className={audienceType === 'campus' ? 'active' : ''}
                    onClick={() => setAudienceType('campus')}>
                    By Campus
                  </button>
                  <button
                    className={audienceType === 'tag' ? 'active' : ''}
                    onClick={() => setAudienceType('tag')}>
                    By Tag
                  </button>
                </div>
              </div>

              {audienceType === 'individual' && (
                <div className="member-search-container">
                  {emailToName ? (
                    <div className="selected-member">
                      <span>✉️ {emailToName}</span>
                      <button onClick={() => { setEmailTo(''); setEmailToName(''); setMemberSearch(''); }} className="tag-add-btn">✕</button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="text"
                        placeholder="Search by name..."
                        value={memberSearch}
                        onChange={e => setMemberSearch(e.target.value)}
                      />
                      {memberSearch && (
                        <div className="member-search-results">
                          {filteredMembers.length === 0 ? (
                            <div className="search-result-item">No members found</div>
                          ) : (
                            filteredMembers.map(m => (
                              <div
                                key={m.id}
                                className="search-result-item"
                                onClick={() => {
                                  setEmailTo(m.email)
                                  setEmailToName(`${m.first_name} ${m.last_name}`)
                                  setMemberSearch('')
                                }}>
                                {m.first_name} {m.last_name} — {m.email}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {audienceType === 'campus' && (
                <select
                  value={selectedCampus}
                  onChange={e => setSelectedCampus(e.target.value)}>
                  <option value="">Select a campus...</option>
                  {campuses.map(campus => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name}
                    </option>
                  ))}
                </select>
              )}

              {audienceType === 'tag' && (
                <select
                  value={selectedTag}
                  onChange={e => setSelectedTag(e.target.value)}>
                  <option value="">Select a tag...</option>
                  {tags.map(tag => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              )}

              <input
                type="text"
                placeholder="Subject"
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
              />
              <textarea
                placeholder="Message"
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
              />
              <button onClick={sendEmail} className="send-btn">Send Email</button>
              {emailStatus && <p className="status">{emailStatus}</p>}
            </div>

            <div style={{marginTop: '32px'}}>
              <h3 style={{marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#1F3D34'}}>Email History</h3>
              {emailHistory.length === 0 ? (
                <p style={{color: '#8FAF9C'}}>No emails sent yet.</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Audience</th>
                      <th>Recipients</th>
                      <th>Sent At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailHistory.map(email => (
                      <tr key={email.id}>
                        <td>{email.subject}</td>
                        <td>{email.audience_type}</td>
                        <td>{email.recipient_count}</td>
                        <td>{new Date(email.sent_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;